// Limpeza de valores colados nas variáveis de templates do WhatsApp.
// A Meta rejeita parâmetros com quebra de linha, tab ou mais de 4 espaços
// consecutivos, e copy-paste de CRMs costuma trazer HTML e caracteres invisíveis.

const HTML_BREAK_TAGS = /<\s*(br|\/p|\/div|\/li|\/tr|\/h[1-6])\b[^<>]*>/gi;
const HTML_TAGS = /<\/?[a-z][a-z0-9-]*(\s[^<>]*)?\/?>/gi;
const HTML_ENTITIES = {
  nbsp: ' ',
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
};
const HTML_ENTITY = /&(#x[0-9a-f]+|#\d+|[a-z]+);/gi;

const LINE_BREAKS_AND_TABS = /[\r\n\t\v\f\u2028\u2029]/g;
const UNICODE_SPACES = /[\u00A0\u1680\u2000-\u200A\u202F\u205F\u3000]/g;
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001F\u007F-\u009F]/g;
// Mantém U+200D (ZWJ) e U+FE0F, que fazem parte de emojis compostos
const INVISIBLE_CHARS = /[\u00AD\u200B\u200E\u200F\u202A-\u202E\u2060\u2066-\u2069\uFEFF]/g;
const MULTIPLE_SPACES = / {2,}/g;

const decodeHtmlEntity = (match, entity) => {
  const lower = entity.toLowerCase();
  if (lower.startsWith('#')) {
    const codePoint = lower.startsWith('#x')
      ? parseInt(lower.slice(2), 16)
      : parseInt(lower.slice(1), 10);
    try {
      return String.fromCodePoint(codePoint);
    } catch {
      return match;
    }
  }
  return HTML_ENTITIES[lower] ?? match;
};

// Entidades são decodificadas antes de remover as tags para que
// marcação escapada (&lt;b&gt;) também seja removida.
const sanitizeOnce = value =>
  value
    .replace(HTML_ENTITY, decodeHtmlEntity)
    .replace(HTML_BREAK_TAGS, ' ')
    .replace(HTML_TAGS, '')
    .replace(LINE_BREAKS_AND_TABS, ' ')
    .replace(UNICODE_SPACES, ' ')
    .replace(CONTROL_CHARS, '')
    .replace(INVISIBLE_CHARS, '')
    .replace(MULTIPLE_SPACES, ' ')
    .trim();

// Texto real estabiliza em 1 ou 2 passadas; o limite evita custo quadrático
// com escapes aninhados (&amp;amp;amp;...). Igual ao backend.
const MAX_SANITIZE_PASSES = 5;

// Uma passada pode formar nova marcação (&amp;lt;, <<b>b>), então repete até
// estabilizar. Isso garante que sanitizar de novo, no blur ou no backend, não
// muda o valor.
export const sanitizeTemplateParam = value => {
  if (typeof value !== 'string') return value;

  let current = value;
  for (let pass = 0; pass < MAX_SANITIZE_PASSES; pass += 1) {
    const sanitized = sanitizeOnce(current);
    if (sanitized === current) break;
    current = sanitized;
  }
  return current;
};

// Sanitiza um trecho colado sem o trim, mantendo nas bordas no máximo um
// espaço como separador do texto vizinho. Os pontos servem de sentinela.
const sanitizePastedFragment = text =>
  sanitizeTemplateParam(`.${text}.`).slice(1, -1);

// Substitui a seleção atual do input pelo texto colado já sanitizado
// e devolve o novo valor e a posição do cursor.
export const insertSanitizedPaste = (input, pastedText) => {
  const currentValue = input.value || '';
  const start = input.selectionStart ?? currentValue.length;
  const end = input.selectionEnd ?? currentValue.length;
  const before = currentValue.slice(0, start);
  const after = currentValue.slice(end);

  let sanitized = sanitizePastedFragment(pastedText);
  // Evita espaço duplicado quando o texto vizinho já tem o separador
  if (before.endsWith(' ')) sanitized = sanitized.replace(/^ /, '');
  if (after.startsWith(' ')) sanitized = sanitized.replace(/ $/, '');

  return {
    value: `${before}${sanitized}${after}`,
    cursor: start + sanitized.length,
  };
};
